import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  List as Menu,
  UserCircle,
  X,
} from '@phosphor-icons/react';
import { Button } from './ui/button';
import { config } from '../config/runtimeConfig';
import { useTranslation } from '../i18n/I18nContext';
import { LanguageSelector } from './landing/LanguageSelector';
import '../styles/site-chrome.css';

const Header = () => {
  const { t } = useTranslation();
  const location = useLocation();
  // Вход в панель — только на витрине (#1335): на страницах дилера по токену
  // это чужая для покупателя кнопка.
  const demoPanelUrl = config.IS_OWN_STOCK ? config.DEMO_PANEL_URL : null;
  // Кабинет открывается в новой вкладке — читалке это говорим в имени ссылки.
  const panelLabel = `${t('showcase.nav.login', 'Log in')} — ${t(
    'showcase.a11y.newTab',
    'opens in a new tab'
  )}`;

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const headerRef = useRef(null);

  /**
   * Шапка едет вместе с содержимым и возвращается накладной на прокрутке
   * ВВЕРХ (#1330). Прилипшая шапка постоянно занимала верх первого экрана;
   * теперь она уходит и приходит по надобности.
   *
   * Порог в высоту самой шапки нужен, чтобы на самом верху страницы она
   * оставалась обычной: иначе на первом же пикселе прокрутки вверх она
   * становилась накладной поверх собственного места и заметно дёргалась.
   *
   * Замер направления — через rAF: `scroll` приходит чаще кадра, и считать
   * на каждом событии значит делать работу, которую никто не увидит.
   */
  // На главной шапка уезжает и возвращается; на остальных страницах прилипает.
  const isHome = location.pathname === '/';
  const [pinned, setPinned] = useState(false);
  useEffect(() => {
    if (!isHome) {
      setPinned(false);
      return undefined;
    }
    let last = window.scrollY;
    let ticking = false;

    const measure = () => {
      ticking = false;
      const y = window.scrollY;
      const height = headerRef.current?.offsetHeight ?? 0;
      const goingUp = y < last;
      last = y;
      setPinned(goingUp && y > height);
    };

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(measure);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [isHome]);

  // На остальных страницах витрины шапка прилипает, но прячется при прокрутке
  // вниз и возвращается при прокрутке вверх (#1335): когда смотрят машины,
  // меню не занимает верх экрана. Вместе с ней прячется прилипшая панель
  // фильтров каталога — состояние уходит в `data-chrome-hidden` на `<html>`,
  // по нему её сдвигает CSS. У дилера шапка прилипшая, как раньше.
  const hideOnScroll = config.IS_OWN_STOCK && !isHome;
  const [hidden, setHidden] = useState(false);
  useEffect(() => {
    if (!hideOnScroll) {
      setHidden(false);
      return undefined;
    }
    let last = window.scrollY;
    let ticking = false;
    const measure = () => {
      ticking = false;
      const y = window.scrollY;
      // Дрожание тачпада в пару пикселей не должно дёргать шапку.
      if (Math.abs(y - last) < 4) return;
      const height = headerRef.current?.offsetHeight ?? 0;
      setHidden(y > last && y > height);
      last = y;
    };
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(measure);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [hideOnScroll]);
  // Раскрытое меню на телефоне не прячем: его открыли, чтобы им пользоваться.
  const chromeHidden = hidden && !isMenuOpen;
  useEffect(() => {
    if (!hideOnScroll) return undefined;
    document.documentElement.toggleAttribute('data-chrome-hidden', chromeHidden);
    return () => document.documentElement.removeAttribute('data-chrome-hidden');
  }, [hideOnScroll, chromeHidden]);

  // Сообщаем свою РЕАЛЬНУЮ высоту в `--header-height` (#1337 follow-up).
  //
  // Переменную читает `CarsPage.getHeaderHeight()`, чтобы прижать sticky-панель
  // фильтров под шапку. Писал её только `bootstrap/headerHeight.js` — то есть
  // ТОЛЬКО в режиме виджета, где она компенсирует фикс-шапку сайта дилера. На
  // standalone-витрине переменной не было, и getHeaderHeight падал на хардкод
  // `64px` — высоту шапки ВИДЖЕТА. Наша шапка 88px, поэтому фильтры
  // прилипали на 24px выше и наезжали на логотип.
  //
  // Меряем, а не берём константу: высота зависит от брейкпоинта, и любая
  // будущая правка шапки не должна снова разъезжаться с фильтрами.
  // ResizeObserver — потому что смена ширины меняет высоту без ремаунта.
  useEffect(() => {
    const el = headerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const write = () => {
      const h = Math.round(el.getBoundingClientRect().height);
      if (h > 0)
        document.documentElement.style.setProperty('--header-height', `${h}px`);
    };
    write();
    const observer = new ResizeObserver(write);
    observer.observe(el);
    return () => {
      observer.disconnect();
      // Снимаем за собой: на chromeless-страницах шапки нет, и оставленное
      // значение соврало бы тому, кто прочитает переменную следующим.
      document.documentElement.style.removeProperty('--header-height');
    };
  }, []);

  // Service-центр — за флагом ENABLE_SERVICE_CENTER: без него роут /service
  // в App.jsx вообще не регистрируется. Ссылка же стояла безусловно, а
  // catch-all роута нет — на demo.shiftgears.ai (флаг там не выставлен) пункт
  // «Service» вёл на пустой экран между шапкой и подвалом (#1323).
  // Пункта «Home» нет намеренно: на главную ведёт логотип — так устроена
  // навигация на всех разобранных дилерских сайтах, и дублировать её
  // отдельной ссылкой значит тратить место в меню на то, куда и так можно
  // попасть кликом по названию.
  // Меню по структуре с синка #237 ([37:41]): «Contact» заменён на
  // «Chat with us», рядом появился «Showroom». Это демо, и разделы названы по
  // фичам, которые показываем, а не по привычным ярлыкам дилерского сайта.
  // Салоны, карта и QR уехали в «Showroom» — приехать и написать это разные
  // намерения, и один раздел «связаться» их смешивал.
  const navigation = [
    { name: t('showcase.nav.catalog', 'Catalog'), href: '/cars' },
    { name: t('showcase.nav.chat', 'Chat with us'), href: '/contact' },
    { name: t('showcase.nav.showroom', 'Showroom'), href: '/showrooms' },
    // «Company» крайний справа и назван по содержимому: страница про
    // ShiftGears, а не про вымышленный салон, каким читалось «About Us».
    { name: t('showcase.nav.company', 'Company'), href: '/about' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    // Своя подложка ВЕЗДЕ, включая главную, и шапка стоит в потоке НАД кадром.
    // Короткое время она лежала на кадре: кадр шёл до самого верха страницы, а
    // логотип и правая группа стояли на белых полках, врезанных в снимок. Приём
    // сняли по просьбе автора витрины — просветы снимка между полками читались
    // тремя отдельными блоками, а не одной шапкой.
    <header
      className={`site-header${isHome ? '' : ' site-header--sticky'}${
        pinned ? ' site-header--pinned' : ''
      }${chromeHidden ? ' site-header--hidden' : ''}`}
      ref={headerRef}
    >
      <div className="site-chrome__container">
        <div className="site-header__row">
          {/* Logo */}
          <div className="site-header__side">
            <Link to="/" className="site-header__logo">
              {/* Логотип для светлого фона (`-light` — чернила под светлую
                  подложку): на витрине шапка везде стоит на светлом, на
                  главной — на полотне над кадром героя в рамке (#1335). У
                  дилера по токену главная прежняя (`DealerHomePage`): там
                  шапка лежит на затемнённом снимке, и на ней нужен `-dark`,
                  пока под шапкой снова не белое (накладная шапка после
                  прокрутки вверх, раскрытое меню). */}
              <img
                src={`/assets/logo-horizontal-${
                  !config.IS_OWN_STOCK && isHome && !pinned && !isMenuOpen
                    ? 'dark'
                    : 'light'
                }.svg`}
                alt="ShiftGears"
              />
            </Link>
          </div>

          <div className="site-header__side site-header__side--end">
            {/* Desktop Navigation */}
            <nav className="site-header__nav">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`site-header__link${
                    isActive(item.href) ? ' site-header__link--active' : ''
                  }`}
                >
                  {item.name}
                </Link>
              ))}
            </nav>

            {/* Переключатель языка. Компонент готовый, тот же что на лендинге
              машины: заводить второй ради шапки незачем.

              В строке шапки он живёт только с 900px, вместе с меню: на
              телефоне справа остаются лупа и бургер, а язык уезжает внутрь
              меню (ниже) — там он рядом с пунктами, к которым и относится. */}
            <div className="site-header__lang site-header__lang--row">
              <LanguageSelector />
            </div>

            {/* Вход в кабинет — демо-панель дилера на фикстурах (#1339), путь
                этой же витрины (/panel/). Ссылка, а не кнопка: это переход.
                В новой вкладке (#1335), как пункт в подвале: витрина и чат
                посетителя остаются открытыми рядом с кабинетом.
                Иконка с подписью: человечек сам по себе не говорит, что это
                вход. Без адреса в конфиге кнопки нет — DEMO_PANEL_URL. */}
            {demoPanelUrl && (
              <>
                {/* С 1024px — чёрная таблетка со стрелкой (#1335): синий на
                    странице остаётся у поиска и главных действий; ниже —
                    круглая иконка:
                    подпись на телефоне не помещается. Видна одна из двух —
                    вторая `display: none` и из дерева доступности уходит. */}
                <Button
                  as="a"
                  href={demoPanelUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={panelLabel}
                  size="sm"
                  variant="dark"
                  arrow
                  className="site-header__login"
                >
                  {t('showcase.nav.login', 'Log in')}
                </Button>
                <a
                  href={demoPanelUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={panelLabel}
                  className="site-header__account"
                >
                  <UserCircle size={20} />
                  <span className="site-header__account-label">
                    {t('showcase.nav.login', 'Log in')}
                  </span>
                </a>
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="site-header__burger">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </Button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="site-header__burger">
            <div className="site-header__mobile-nav">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`site-header__mobile-link${
                    isActive(item.href)
                      ? ' site-header__mobile-link--active'
                      : ''
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {item.name}
                </Link>
              ))}

              {/* Язык — последним пунктом меню, отделённый линией: он не
                  страница сайта, а настройка, и вставать в один ряд с
                  «Каталогом» ему незачем. */}
              <div className="site-header__lang site-header__lang--menu">
                <LanguageSelector />
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
